import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { firstValueFrom } from 'rxjs';
import { ToastService } from 'src/app/services/toast.service';
import { TextareaModule } from 'primeng/textarea';
import { DonationService, EVHub } from 'src/app/services/donation-service';
import { 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonButtons, 
  IonButton, 
  IonIcon, 
  IonContent,
  IonModal 
} from '@ionic/angular/standalone';

@Component({
  selector: 'app-donation-sheet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    SelectModule,
    CheckboxModule,
    TextareaModule,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent
  ],
  templateUrl: './donation-sheet.component.html',
  styleUrls: ['./donation-sheet.component.scss']
})
export class DonationSheetComponent implements OnInit {
  @Input() visible = false;
  @Output() onClose = new EventEmitter<void>();
  @Output() onDonationSuccess = new EventEmitter<void>();
  @ViewChild(IonModal) modal!: IonModal;

  donationForm!: FormGroup;
  evHubs: EVHub[] = [];
  isLoading = false;
  isSubmitting = false;

  predefinedAmounts = [
    { label: '5 €', value: 500 },
    { label: '10 €', value: 1000 },
    { label: '25 €', value: 2500 },
    { label: '50 €', value: 5000 }
  ];

  selectedPredefinedAmount: number | null = null;

  constructor(
    private fb: FormBuilder,
    private donationService: DonationService,
    private toast: ToastService
  ) {}

  ngOnInit() {
    this.initForm();
    this.loadEVHubs();
  }

  initForm() {
    this.donationForm = this.fb.group({
      businessHubId: [null, [Validators.required]],
      amount: [1000, [Validators.required, Validators.min(100)]],
      message: ['', [Validators.maxLength(500)]],
      isAnonymous: [false]
    });
  }

  async loadEVHubs() {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.donationService.getEVHubs());
      if (response.success) {
        this.evHubs = response.hubs;
      }
    } catch (error) {
      console.error('Error loading e.V. hubs:', error);
      this.toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: 'Organisationen konnten nicht geladen werden'
      });
    } finally {
      this.isLoading = false;
    }
  }

  selectPredefinedAmount(amount: number) {
    this.selectedPredefinedAmount = amount;
    this.donationForm.patchValue({ amount });
  }

  onAmountChange() {
    const currentAmount = this.donationForm.get('amount')?.value;
    const matchingPredefined = this.predefinedAmounts.find(a => a.value === currentAmount);
    this.selectedPredefinedAmount = matchingPredefined ? currentAmount : null;
  }

  get amountInEuros(): number {
    const amount = this.donationForm.get('amount')?.value || 0;
    return amount / 100;
  }

  async submitDonation() {
    if (this.donationForm.invalid || this.isSubmitting) return;

    this.isSubmitting = true;

    try {
      const response = await firstValueFrom(
        this.donationService.createDonation(this.donationForm.value)
      );

      if (response.success) {
        this.toast.add({
          severity: 'success',
          summary: 'Spende erfolgreich!',
          detail: `Vielen Dank für deine Unterstützung! 🎉`
        });
        
        this.onDonationSuccess.emit();
        this.close();
        this.resetForm();
      } else {
        throw new Error(response.error || 'Spende fehlgeschlagen');
      }
    } catch (error: any) {
      console.error('Error creating donation:', error);
      this.toast.add({
        severity: 'error',
        summary: 'Fehler',
        detail: error.error?.error || error.message || 'Spende konnte nicht verarbeitet werden'
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  resetForm() {
    this.donationForm.reset({
      businessHubId: null,
      amount: 1000,
      message: '',
      isAnonymous: false
    });
    this.selectedPredefinedAmount = null;
  }

  close() {
    this.onClose.emit();
  }
}